import React, {Component} from 'react';
import PageHeader from "../PageHeader";
import Menu from "../Menu";
import Preloader from "../Preloader";
import api from "../../api";
import './CreateRecord.scss';

type ServiceItem = {
    id: number,
    title: string,
    price?: number | null,
    length?: number | null,
    priceDisplay?: string,
    lengthDisplay?: string,
    staffIds: number[],
    staffAvailability?: {
        [staffId: string]: {
            available: boolean,
            source: string,
            reasonCode: string,
            reasonMessage: string,
        }
    },
}

type ServiceGroupItem = {
    id: number,
    title: string,
    services: ServiceItem[],
}

type StaffItem = {
    id: number,
    name: string,
    isCurrent: boolean,
    specializations: string[],
    serviceIds: number[],
}

type CompanyItem = {
    id: number,
    title: string,
    address: string,
    city: string,
    ownStaffId: number | null,
    staff: StaffItem[],
    serviceGroups: ServiceGroupItem[],
}

type BootstrapResponse = {
    defaultCompanyId: number | null,
    defaultStaffId: number | null,
    companies: CompanyItem[],
}

export class CreateRecord extends Component<any, any> {
    constructor(props: any) {
        super(props);
        window.scrollTo(0, 0);

        this.state = {
            isBootLoading: true,
            isSubmitting: false,
            loadError: '',
            submitError: '',
            successMessage: '',
            bootstrap: null as BootstrapResponse | null,
            selectedCompanyId: 0,
            selectedStaffId: 0,
            selectedServiceIds: [] as number[],
            clientPhone: '',
            clientName: '',
            recordDate: '',
            recordTime: '',
            comment: '',
            createdBooking: null as any,
        };
    }

    componentDidMount() {
        this.loadBootstrap();
    }

    loadBootstrap = () => {
        this.setState({
            isBootLoading: true,
            loadError: '',
        });

        api({
            url: '/widget/staff/v2/profile/record/bootstrap',
            method: 'GET',
        }).then((result) => {
            const bootstrap = result.data as BootstrapResponse;
            const companies = Array.isArray(bootstrap?.companies) ? bootstrap.companies : [];
            const selectedCompanyId = bootstrap?.defaultCompanyId || (companies[0] ? companies[0].id : 0);
            const selectedCompany = companies.find((company) => company.id === selectedCompanyId);
            const selectedStaffId = this.getDefaultStaffId(selectedCompany, bootstrap?.defaultStaffId || 0);

            this.setState({
                isBootLoading: false,
                bootstrap,
                selectedCompanyId,
                selectedStaffId,
            });
        }).catch((error) => {
            const message = error?.response?.data?.message || 'Не удалось загрузить форму записи. Попробуйте ещё раз.';
            this.setState({
                isBootLoading: false,
                loadError: message,
            });
        });
    }

    getDefaultStaffId = (company: CompanyItem | undefined, preferredStaffId: number) => {
        if (!company) {
            return 0;
        }

        if (preferredStaffId > 0) {
            if (preferredStaffId === 1 && company.staff.length > 1) {
                return 1;
            }

            const preferred = company.staff.find((staff) => staff.id === preferredStaffId);
            if (preferred) {
                return preferred.id;
            }
        }

        if (company.ownStaffId) {
            return company.ownStaffId;
        }

        if (company.staff.length > 1) {
            return 1;
        }

        return company.staff[0] ? company.staff[0].id : 0;
    }

    getSelectedCompany = (): CompanyItem | undefined => {
        const bootstrap = this.state.bootstrap as BootstrapResponse | null;
        if (!bootstrap || !Array.isArray(bootstrap.companies)) {
            return undefined;
        }

        return bootstrap.companies.find((company) => company.id === Number(this.state.selectedCompanyId));
    }

    getSelectedStaff = (): StaffItem | undefined => {
        const company = this.getSelectedCompany();
        if (!company) {
            return undefined;
        }

        return company.staff.find((staff) => staff.id === Number(this.state.selectedStaffId));
    }

    getServiceById = (company: CompanyItem | undefined, serviceId: number): ServiceItem | undefined => {
        if (!company) {
            return undefined;
        }

        for (let i = 0; i < company.serviceGroups.length; i++) {
            const group = company.serviceGroups[i];
            const service = group.services.find((item) => item.id === serviceId);
            if (service) {
                return service;
            }
        }

        return undefined;
    }

    isServiceAvailable = (service: ServiceItem): boolean => {
        const selectedStaffId = Number(this.state.selectedStaffId);

        if (selectedStaffId > 1) {
            return service.staffIds.includes(selectedStaffId);
        }

        return service.staffIds.length > 0;
    }

    getServiceUnavailableReason = (service: ServiceItem): string => {
        const selectedStaffId = Number(this.state.selectedStaffId);

        if (selectedStaffId > 1) {
            const availability = service.staffAvailability ? service.staffAvailability[String(selectedStaffId)] : null;
            if (availability && !availability.available && availability.reasonMessage) {
                return availability.reasonMessage;
            }

            if (!service.staffIds.length) {
                return 'В филиале нет мастеров, у которых эта услуга доступна для записи.';
            }

            return 'У выбранного мастера услуга недоступна для записи.';
        }

        if (!service.staffIds.length) {
            return 'В филиале нет мастеров, у которых эта услуга доступна для записи.';
        }

        return '';
    }

    getServiceStateLabel = (service: ServiceItem, isAvailable: boolean, isChecked: boolean): string => {
        if (isChecked) {
            return 'Выбрано';
        }

        if (isAvailable) {
            return 'Выбрать';
        }

        const selectedStaffId = Number(this.state.selectedStaffId);
        if (selectedStaffId > 1) {
            const availability = service.staffAvailability ? service.staffAvailability[String(selectedStaffId)] : null;
            if (availability?.reasonCode === 'excluded_for_staff') {
                return 'Исключена';
            }

            if (availability?.reasonCode === 'not_in_specialization') {
                return 'Не по профилю';
            }
        }

        if (!service.staffIds.length) {
            return 'Нет мастеров';
        }

        return 'Недоступно';
    }

    filterSelectedServices = (serviceIds: number[], company: CompanyItem | undefined, staffId: number) => {
        if (!company) {
            return [];
        }

        return serviceIds.filter((serviceId) => {
            const service = this.getServiceById(company, serviceId);
            if (!service) {
                return false;
            }

            if (staffId > 1) {
                return service.staffIds.includes(staffId);
            }

            return service.staffIds.length > 0;
        });
    }

    handleCompanyChange = (event: any) => {
        const selectedCompanyId = Number(event.target.value) || 0;
        const bootstrap = this.state.bootstrap as BootstrapResponse | null;
        const company = bootstrap?.companies.find((item) => item.id === selectedCompanyId);
        const selectedStaffId = this.getDefaultStaffId(company, 0);

        this.setState({
            selectedCompanyId,
            selectedStaffId,
            selectedServiceIds: [],
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    handleStaffChange = (event: any) => {
        const selectedStaffId = Number(event.target.value) || 0;
        const company = this.getSelectedCompany();
        const selectedServiceIds = this.filterSelectedServices(this.state.selectedServiceIds, company, selectedStaffId);

        this.setState({
            selectedStaffId,
            selectedServiceIds,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    toggleService = (serviceId: number) => {
        const company = this.getSelectedCompany();
        const service = this.getServiceById(company, serviceId);
        if (!service || !this.isServiceAvailable(service)) {
            return;
        }

        const selectedServiceIds = [...this.state.selectedServiceIds];
        const index = selectedServiceIds.indexOf(serviceId);
        if (index >= 0) {
            selectedServiceIds.splice(index, 1);
        } else {
            selectedServiceIds.push(serviceId);
        }

        this.setState({
            selectedServiceIds,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    handleFieldChange = (fieldName: string) => (event: any) => {
        this.setState({
            [fieldName]: event.target.value,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });
    }

    normalizePhone = (phone: string) => {
        const digits = String(phone || '').replace(/\D+/g, '');
        if (digits.length === 11 && digits.charAt(0) === '8') {
            return '7' + digits.slice(1);
        }

        return digits;
    }

    getSelectedServicesSummary = () => {
        const company = this.getSelectedCompany();
        const selectedServiceIds = this.state.selectedServiceIds as number[];
        let totalLength = 0;
        const selectedServices: ServiceItem[] = [];

        selectedServiceIds.forEach((serviceId) => {
            const service = this.getServiceById(company, serviceId);
            if (!service) {
                return;
            }

            selectedServices.push(service);
            totalLength += Number(service.length || 0);
        });

        return {
            selectedServices,
            totalLength,
        };
    }

    handleSubmit = () => {
        const company = this.getSelectedCompany();
        const selectedCompanyId = Number(this.state.selectedCompanyId);
        const selectedStaffId = Number(this.state.selectedStaffId);
        const selectedServiceIds = this.state.selectedServiceIds as number[];
        const clientName = String(this.state.clientName || '').trim();
        const clientPhone = String(this.state.clientPhone || '').trim();
        const recordDate = String(this.state.recordDate || '').trim();
        const recordTime = String(this.state.recordTime || '').trim();
        const comment = String(this.state.comment || '').trim();

        if (!company || selectedCompanyId <= 0) {
            this.setState({submitError: 'Выберите филиал.'});
            return;
        }

        if (selectedStaffId <= 0) {
            this.setState({submitError: 'Выберите мастера.'});
            return;
        }

        if (!selectedServiceIds.length) {
            this.setState({submitError: 'Выберите хотя бы одну услугу.'});
            return;
        }

        if (!recordDate || !recordTime) {
            this.setState({submitError: 'Выберите дату и время записи.'});
            return;
        }

        if (!clientName) {
            this.setState({submitError: 'Укажите имя клиента.'});
            return;
        }

        if (this.normalizePhone(clientPhone).length !== 11) {
            this.setState({submitError: 'Введите корректный номер телефона клиента.'});
            return;
        }

        this.setState({
            isSubmitting: true,
            submitError: '',
            successMessage: '',
            createdBooking: null,
        });

        api({
            url: '/widget/staff/v2/profile/record/create',
            method: 'POST',
            data: {
                companyId: selectedCompanyId,
                booking: {
                    companyId: selectedCompanyId,
                    resourceId: selectedStaffId,
                    serviceIds: selectedServiceIds,
                    datetime: `${recordDate} ${recordTime}`,
                },
                client: {
                    phone: clientPhone,
                    name: clientName,
                },
                commentRecord: comment,
            },
        }).then((result) => {
            this.setState({
                isSubmitting: false,
                successMessage: result?.data?.message || 'Запись создана.',
                createdBooking: result?.data?.booking || null,
                clientName: '',
                clientPhone: '',
                comment: '',
            });
        }).catch((error) => {
            this.setState({
                isSubmitting: false,
                submitError: error?.response?.data?.message || 'Не удалось создать запись. Попробуйте ещё раз.',
            });
        });
    }

    renderServiceGroups() {
        const company = this.getSelectedCompany();
        if (!company) {
            return null;
        }

        if (!company.serviceGroups.length) {
            return (
                <div className='create-record-empty'>
                    Для этого филиала пока не найдено активных услуг.
                </div>
            );
        }

        return company.serviceGroups.map((group) => (
            <div className='create-record-services-group' key={group.id}>
                <div className='create-record-group-title'>{group.title}</div>
                <div className='create-record-services-list'>
                    {group.services.map((service) => {
                        const isChecked = this.state.selectedServiceIds.includes(service.id);
                        const isAvailable = this.isServiceAvailable(service);
                        const unavailableReason = !isAvailable ? this.getServiceUnavailableReason(service) : '';
                        const stateLabel = this.getServiceStateLabel(service, isAvailable, isChecked);

                        return (
                            <button
                                type='button'
                                key={service.id}
                                className={`create-record-service-item${isChecked ? ' is-active' : ''}${!isAvailable ? ' is-disabled' : ''}`}
                                onClick={() => this.toggleService(service.id)}
                                disabled={!isAvailable}
                            >
                                <span className='create-record-service-main'>
                                    <span className='create-record-service-title'>{service.title}</span>
                                    <span className='create-record-service-meta'>
                                        {service.lengthDisplay ? `${service.lengthDisplay} мин` : 'Длительность по салону'}
                                        {service.priceDisplay ? ` · ${service.priceDisplay} р` : ''}
                                    </span>
                                    {!isAvailable && unavailableReason ? (
                                        <span className='create-record-service-reason'>{unavailableReason}</span>
                                    ) : null}
                                </span>
                                <span className='create-record-service-check'>
                                    {stateLabel}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        ));
    }

    renderSelectedServices() {
        const summary = this.getSelectedServicesSummary();

        if (!summary.selectedServices.length) {
            return (
                <div className='create-record-empty'>
                    Выберите услуги, которые клиент хочет получить.
                </div>
            );
        }

        return (
            <div>
                <div className='create-record-summary-row'>
                    <span>Выбрано услуг</span>
                    <strong>{summary.selectedServices.length}</strong>
                </div>
                <div className='create-record-summary-row'>
                    <span>Общая длительность</span>
                    <strong>{summary.totalLength || 0} мин</strong>
                </div>

                <div className='create-record-selected-services'>
                    {summary.selectedServices.map((service) => (
                        <div className='create-record-selected-service' key={service.id}>
                            <span>{service.title}</span>
                            <button type='button' onClick={() => this.toggleService(service.id)}>
                                Убрать
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderSuccess() {
        const createdBooking = this.state.createdBooking;
        if (!createdBooking) {
            return null;
        }

        return (
            <div className='create-record-card create-record-success'>
                <div className='create-record-success-title'>Клиент записан</div>
                <div className='create-record-success-text'>{this.state.successMessage}</div>
                <div className='create-record-success-text'>
                    Дата и время: <strong>{createdBooking.datetime}</strong>
                </div>
                <div className='create-record-success-actions'>
                    <button
                        type='button'
                        className='btn btn-outline-dark'
                        onClick={() => this.setState({createdBooking: null, successMessage: '', selectedServiceIds: []})}
                    >
                        Новая запись
                    </button>
                    <a className='btn btn-dark' href='/records'>К моим записям</a>
                </div>
            </div>
        );
    }

    render() {
        if (this.state.isBootLoading) {
            return (<Preloader></Preloader>);
        }

        const company = this.getSelectedCompany();
        const selectedStaff = this.getSelectedStaff();
        const canUseAnyMaster = company && company.staff.length > 1;

        return (
            <div className='main schedule create-record-page'>
                <div className='content mx-w-content position-relative'>
                    <PageHeader
                        pageTitle='Записать клиента'
                        backBtnUrl='/'
                    />

                    <div className='pt-header'></div>

                    <div className='ml-3 mr-3 pb-5'>
                        <div className='create-record-card create-record-hint'>
                            <div className='create-record-hint-title'>Быстрая запись клиента</div>
                            <div className='create-record-hint-text'>
                                Подтверждение по коду не требуется. Достаточно имени и номера телефона клиента.
                            </div>
                        </div>

                        {this.state.loadError ? (
                            <div className='create-record-card create-record-error'>
                                <div className='create-record-error-text'>{this.state.loadError}</div>
                                <button type='button' className='btn btn-dark mt-3' onClick={this.loadBootstrap}>
                                    Обновить
                                </button>
                            </div>
                        ) : null}

                        {!this.state.loadError && (!this.state.bootstrap || !this.state.bootstrap.companies.length) ? (
                            <div className='create-record-card create-record-empty'>
                                Для вашей учётной записи пока не найдено доступных филиалов.
                            </div>
                        ) : null}

                        {!this.state.loadError && this.state.bootstrap && this.state.bootstrap.companies.length ? (
                            <>
                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Параметры записи</div>

                                    <label className='create-record-label'>Филиал</label>
                                    <select
                                        className='form-select create-record-input'
                                        value={this.state.selectedCompanyId}
                                        onChange={this.handleCompanyChange}
                                    >
                                        <option value={0}>Выберите филиал</option>
                                        {this.state.bootstrap.companies.map((companyItem: CompanyItem) => (
                                            <option key={companyItem.id} value={companyItem.id}>
                                                {companyItem.city}, {companyItem.address}
                                            </option>
                                        ))}
                                    </select>

                                    <label className='create-record-label'>Мастер</label>
                                    <select
                                        className='form-select create-record-input'
                                        value={this.state.selectedStaffId}
                                        onChange={this.handleStaffChange}
                                    >
                                        <option value={0}>Выберите мастера</option>
                                        {canUseAnyMaster ? <option value={1}>Любой подходящий мастер</option> : null}
                                        {company?.staff.map((staffItem) => (
                                            <option key={staffItem.id} value={staffItem.id}>
                                                {staffItem.name}{staffItem.isCurrent ? ' · мой профиль' : ''}
                                            </option>
                                        ))}
                                    </select>

                                    {selectedStaff && selectedStaff.specializations.length ? (
                                        <div className='create-record-inline-note'>
                                            Специализации: {selectedStaff.specializations.join(', ')}
                                        </div>
                                    ) : null}

                                    <div className='create-record-grid'>
                                        <div>
                                            <label className='create-record-label'>Дата</label>
                                            <input
                                                className='form-control create-record-input'
                                                type='date'
                                                value={this.state.recordDate}
                                                onChange={this.handleFieldChange('recordDate')}
                                            />
                                        </div>
                                        <div>
                                            <label className='create-record-label'>Время</label>
                                            <input
                                                className='form-control create-record-input'
                                                type='time'
                                                value={this.state.recordTime}
                                                onChange={this.handleFieldChange('recordTime')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Клиент</div>

                                    <label className='create-record-label'>Имя клиента</label>
                                    <input
                                        type='text'
                                        className='form-control create-record-input'
                                        placeholder='Например, Анна'
                                        value={this.state.clientName}
                                        onChange={this.handleFieldChange('clientName')}
                                    />

                                    <label className='create-record-label'>Номер телефона клиента</label>
                                    <input
                                        type='tel'
                                        className='form-control create-record-input'
                                        placeholder='+7 999 123-45-67'
                                        value={this.state.clientPhone}
                                        onChange={this.handleFieldChange('clientPhone')}
                                    />

                                    <label className='create-record-label'>Комментарий</label>
                                    <textarea
                                        className='form-control create-record-input create-record-textarea'
                                        placeholder='Например, клиент пришёл раньше или просил конкретного мастера'
                                        value={this.state.comment}
                                        onChange={this.handleFieldChange('comment')}
                                    />
                                </div>

                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Услуги</div>
                                    {this.renderServiceGroups()}
                                </div>

                                <div className='create-record-card'>
                                    <div className='create-record-section-title'>Что попадёт в запись</div>
                                    {this.renderSelectedServices()}
                                </div>

                                {this.state.submitError ? (
                                    <div className='create-record-card create-record-error'>
                                        <div className='create-record-error-text'>{this.state.submitError}</div>
                                    </div>
                                ) : null}

                                {this.renderSuccess()}

                                <button
                                    type='button'
                                    className='btn btn-dark btn-lg create-record-submit'
                                    disabled={this.state.isSubmitting}
                                    onClick={this.handleSubmit}
                                >
                                    {this.state.isSubmitting ? 'Сохраняем запись...' : 'Записать клиента'}
                                </button>
                            </>
                        ) : null}
                    </div>
                </div>
                <Menu></Menu>
            </div>
        );
    }
}

export default CreateRecord;
